import { useEffect, useRef } from "react";
import { useLocation, useNavigate, type Location } from "react-router-dom";
import { parseSettingsPath, settingsPath, useSettingsHub } from "./settings-hub";

type SettingsState = { background?: Location; settingsFrom?: string };

/** 설정 주소 뒤에 남아 있는 화면. 주소가 설정이 아니면 지금 주소다. */
export function useSurfaceLocation(): Location {
  const location = useLocation();
  const state = location.state as SettingsState | null;
  if (location.pathname.startsWith("/settings") && state?.background) return state.background;
  return location;
}

/**
 * 설정 모달을 `/settings/:scope/:section` 과 맞춘다.
 * 화면에서 열면 뒤에 있던 주소를 유지하고, 주소로 들어오면 그 항목을 연다.
 */
export function SettingsUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const scope = useSettingsHub((s) => s.scope);
  const section = useSettingsHub((s) => s.section);
  const locationRef = useRef(location);
  locationRef.current = location;
  const pushed = useRef<string | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    const current = locationRef.current;
    const parsed = parseSettingsPath(current.pathname);
    const hub = useSettingsHub.getState();
    if (current.pathname.startsWith("/settings") && !parsed) {
      navigate("/settings/app/appearance", { replace: true, state: current.state });
      return;
    }
    if (parsed) {
      if (hub.scope !== parsed.scope || hub.section !== parsed.section) hub.open(parsed.scope, parsed.section);
      const canonical = settingsPath(parsed.scope, parsed.section);
      if (current.pathname !== canonical) navigate(canonical, { replace: true, state: current.state });
      pushed.current = canonical;
      return;
    }
    if (hub.scope && pushed.current && current.pathname !== pushed.current) {
      const back = pushed.current;
      pushed.current = null;
      hub.close();
      if (useSettingsHub.getState().scope) {
        pushed.current = back;
        navigate(back, { replace: true, state: current.state });
      }
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!scope) return;
    const current = locationRef.current;
    const path = settingsPath(scope, section);
    if (current.pathname === path) {
      pushed.current = path;
      return;
    }
    const state = (current.state ?? null) as SettingsState | null;
    const onSettings = current.pathname.startsWith("/settings");
    const background = onSettings ? state?.background : current;
    const settingsFrom = onSettings ? state?.settingsFrom : `${current.pathname}${current.search}${current.hash}`;
    pushed.current = path;
    navigate(path, { replace: onSettings, state: { background, settingsFrom } });
  }, [scope, section, navigate]);

  useEffect(() => {
    if (scope) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    pushed.current = null;
    const current = locationRef.current;
    if (!current.pathname.startsWith("/settings")) return;
    const state = (current.state ?? null) as SettingsState | null;
    const back = state?.settingsFrom || (state?.background ? `${state.background.pathname}${state.background.search}${state.background.hash}` : "/");
    navigate(back, { replace: true });
  }, [scope, navigate]);

  return null;
}
